import {
  UserType,
  UserCategory,
  UserStatus,
  VisitorStatus,
  ParentRelation,
  GuestType,
} from './users.interfaces';

export const CATEGORY_DEFAULT_ROLES: Record<UserCategory, string[]> = {
  [UserCategory.VISITOR]: ['visitor'],
  [UserCategory.GUARDIAN]: ['guardian'],
  [UserCategory.STAFF]: ['internal_staff', 'admin_role', 'teacher', 'finance'],
  [UserCategory.EXTERNAL]: ['external_staff', 'government_official'],
  [UserCategory.STUDENT]: ['student'],
};

export const CATEGORY_DISPLAY_LABELS: Record<UserCategory, string> = {
  [UserCategory.VISITOR]: 'Visitor',
  [UserCategory.GUARDIAN]: 'Guardian',
  [UserCategory.STAFF]: 'Staff',
  [UserCategory.EXTERNAL]: 'External',
  [UserCategory.STUDENT]: 'Student',
};

// User type options
export const USER_TYPE_OPTIONS = [
  { value: UserType.PARENTS, label: 'Parents & Guardians' },
  { value: UserType.VISITORS, label: 'Visitors' },
  { value: UserType.GUESTS, label: 'Guests & External' },
];

// Status options
export const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: UserStatus.ACTIVE, label: 'Active' },
  { value: UserStatus.INACTIVE, label: 'Inactive' },
  { value: UserStatus.PENDING, label: 'Pending' },
  { value: UserStatus.BLOCKED, label: 'Blocked' },
];

// Visitor status options
export const VISITOR_STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: VisitorStatus.SCHEDULED, label: 'Scheduled' },
  { value: VisitorStatus.CHECKED_IN, label: 'Checked In' },
  { value: VisitorStatus.CHECKED_OUT, label: 'Checked Out' },
  { value: VisitorStatus.CANCELLED, label: 'Cancelled' },
  { value: VisitorStatus.COMPLETED, label: 'Completed' },
];

// Parent relation options
export const PARENT_RELATION_OPTIONS = [
  { value: 'all', label: 'All Relations' },
  { value: ParentRelation.FATHER, label: 'Father' },
  { value: ParentRelation.MOTHER, label: 'Mother' },
  { value: ParentRelation.GUARDIAN, label: 'Guardian' },
  { value: ParentRelation.STEP_FATHER, label: 'Step Father' },
  { value: ParentRelation.STEP_MOTHER, label: 'Step Mother' },
  { value: ParentRelation.GRANDPARENT, label: 'Grandparent' },
  { value: ParentRelation.OTHER, label: 'Other' },
];

// Guest type options
export const GUEST_TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: GuestType.VENDOR, label: 'Vendor' },
  { value: GuestType.CONTRACTOR, label: 'Contractor' },
  { value: GuestType.SPEAKER, label: 'Speaker' },
  { value: GuestType.VOLUNTEER, label: 'Volunteer' },
  { value: GuestType.OFFICIAL, label: 'Official' },
  { value: GuestType.OTHER, label: 'Other' },
];

// Translation-aware helper functions
export const getTranslatedStatusOptions = (t: (key: string) => string) => [
  { value: 'all', label: t('users.filters.allStatus') },
  { value: UserStatus.ACTIVE, label: t('users.options.status.active') },
  { value: UserStatus.INACTIVE, label: t('users.options.status.inactive') },
  { value: UserStatus.PENDING, label: t('users.options.status.pending') },
  { value: UserStatus.BLOCKED, label: t('users.options.status.blocked') },
];

export const getTranslatedVisitorStatusOptions = (
  t: (key: string) => string
) => [
  { value: 'all', label: t('users.filters.allStatus') },
  {
    value: VisitorStatus.SCHEDULED,
    label: t('users.options.visitorStatus.scheduled'),
  },
  {
    value: VisitorStatus.CHECKED_IN,
    label: t('users.options.visitorStatus.checkedIn'),
  },
  {
    value: VisitorStatus.CHECKED_OUT,
    label: t('users.options.visitorStatus.checkedOut'),
  },
  {
    value: VisitorStatus.CANCELLED,
    label: t('users.options.visitorStatus.cancelled'),
  },
  {
    value: VisitorStatus.COMPLETED,
    label: t('users.options.visitorStatus.completed'),
  },
];

export const getTranslatedParentRelationOptions = (
  t: (key: string) => string
) => [
  { value: 'all', label: t('users.filters.allRelations') },
  { value: ParentRelation.FATHER, label: t('users.options.relation.father') },
  { value: ParentRelation.MOTHER, label: t('users.options.relation.mother') },
  {
    value: ParentRelation.GUARDIAN,
    label: t('users.options.relation.guardian'),
  },
  {
    value: ParentRelation.STEP_FATHER,
    label: t('users.options.relation.stepFather'),
  },
  {
    value: ParentRelation.STEP_MOTHER,
    label: t('users.options.relation.stepMother'),
  },
  {
    value: ParentRelation.GRANDPARENT,
    label: t('users.options.relation.grandparent'),
  },
  { value: ParentRelation.OTHER, label: t('users.options.relation.other') },
];

export const getTranslatedGuestTypeOptions = (t: (key: string) => string) => [
  { value: 'all', label: t('users.filters.allTypes') },
  { value: GuestType.VENDOR, label: t('users.options.guestType.vendor') },
  {
    value: GuestType.CONTRACTOR,
    label: t('users.options.guestType.contractor'),
  },
  { value: GuestType.SPEAKER, label: t('users.options.guestType.speaker') },
  { value: GuestType.VOLUNTEER, label: t('users.options.guestType.volunteer') },
  { value: GuestType.OFFICIAL, label: t('users.options.guestType.official') },
  { value: GuestType.OTHER, label: t('users.options.guestType.other') },
];

// Helper functions
export const getStatusColor = (status: UserStatus): string => {
  switch (status) {
    case UserStatus.ACTIVE:
      return '#4CAF50'; // Green
    case UserStatus.INACTIVE:
      return '#9E9E9E'; // Grey
    case UserStatus.PENDING:
      return '#FF9800'; // Orange
    case UserStatus.BLOCKED:
      return '#F44336'; // Red
    default:
      return '#9E9E9E'; // Grey
  }
};

export const getVisitorStatusColor = (status: VisitorStatus): string => {
  switch (status) {
    case VisitorStatus.SCHEDULED:
      return '#2196F3'; // Blue
    case VisitorStatus.CHECKED_IN:
      return '#FF9800'; // Orange
    case VisitorStatus.CHECKED_OUT:
    case VisitorStatus.COMPLETED:
      return '#4CAF50'; // Green
    case VisitorStatus.CANCELLED:
      return '#F44336'; // Red
    default:
      return '#9E9E9E'; // Grey
  }
};

export const getStatusLabel = (status: UserStatus): string => {
  const option = STATUS_OPTIONS.find((opt) => opt.value === status);
  return option?.label || status;
};

export const getVisitorStatusLabel = (status: VisitorStatus): string => {
  const option = VISITOR_STATUS_OPTIONS.find((opt) => opt.value === status);
  return option?.label || status;
};

export const getParentRelationLabel = (relation: ParentRelation): string => {
  const option = PARENT_RELATION_OPTIONS.find((opt) => opt.value === relation);
  return option?.label || relation;
};

export const getGuestTypeLabel = (type: GuestType): string => {
  const option = GUEST_TYPE_OPTIONS.find((opt) => opt.value === type);
  return option?.label || type;
};

export const formatUserType = (type: UserType): string => {
  const option = USER_TYPE_OPTIONS.find((opt) => opt.value === type);
  return option?.label || type;
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const getUserTypeIcon = (type: UserType): string => {
  switch (type) {
    case UserType.PARENTS:
      return '👨‍👩‍👧‍👦';
    case UserType.VISITORS:
      return '🚶';
    case UserType.GUESTS:
      return '🤝';
    default:
      return '👤';
  }
};

export const getFullName = (firstName: string, lastName: string): string => {
  return `${firstName} ${lastName}`.trim();
};

export const getInitials = (firstName: string, lastName: string): string => {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
};

export const isRecentUser = (
  createdDate: string,
  days: number = 7
): boolean => {
  const created = new Date(createdDate);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - created.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays <= days;
};

export const generateUserSummary = (
  users: any[]
): {
  totalUsers: number;
  averageActivity: number;
  recentUsers: any[];
  mostActive: any[];
} => {
  const totalUsers = users.length;
  const recentUsers = users.filter((u) => isRecentUser(u.createdDate));
  const mostActive = users
    .sort(
      (a, b) =>
        new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime()
    )
    .slice(0, 5);

  return {
    totalUsers,
    averageActivity:
      totalUsers > 0
        ? (users.filter((u) => u.status === UserStatus.ACTIVE).length /
            totalUsers) *
          100
        : 0,
    recentUsers,
    mostActive,
  };
};

export const exportToCSV = (data: any[], filename: string): void => {
  if (!data.length) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map((row) =>
      headers.map((header) => `"${row[header] || ''}"`).join(',')
    ),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

export const validateUserData = (userData: any): string[] => {
  const errors: string[] = [];

  if (!userData.firstName?.trim()) {
    errors.push('First name is required');
  }

  if (!userData.lastName?.trim()) {
    errors.push('Last name is required');
  }

  if (!userData.email?.trim()) {
    errors.push('Email is required');
  } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
    errors.push('Email format is invalid');
  }

  if (!userData.phone?.trim()) {
    errors.push('Phone number is required');
  }

  return errors;
};
